import type { Food } from '@onflow/shared';

// Display-unit resolution. Drinks read in millilitres, everything else in
// grams. The engine unit is always grams (macros are per-100g and 1 ml of a
// water-like drink ≈ 1 g), so this is a labelling convention only — no
// quantity math depends on it.
//
// A food is a drink if it is a beverage, a drinkable milk (dairy milks, not
// powders / condensed / cheeses), or a ready-to-drink supplement. Determined
// from category + name so it covers the whole catalog without a per-row flag.
type FoodLike = Pick<Food, 'category' | 'name'>;

export function isLiquidFood(food: FoodLike | undefined): boolean {
  if (!food) return false;
  const n = food.name.toLowerCase();
  if (food.category === 'beverage') return true;
  if (food.category === 'dairy') {
    return (
      /\bmilk\b/.test(n) &&
      !/(powder|condensed|cheese|mozzarella|khoa|khoya|paneer|evaporated|solid)/.test(n)
    );
  }
  if (food.category === 'supplement') {
    return (
      /(ready to drink|\brtd\b|prepared|shake|isotonic|electrolyte|protein coffee|protein water)/.test(
        n,
      ) && !/\bbar\b|capsule|tablet|gummy/.test(n)
    );
  }
  return false;
}

export function displayUnit(food: FoodLike | undefined): 'g' | 'ml' {
  return isLiquidFood(food) ? 'ml' : 'g';
}

/** "250 ml" for liquids, "28g" for solids (matching the existing grams style). */
export function fmtQuantity(grams: number, unit: 'g' | 'ml'): string {
  return unit === 'ml' ? `${grams} ml` : `${grams}g`;
}
